import { AxiosResponse } from 'axios';
import { all, call, fork, put, takeLatest } from 'redux-saga/effects';

import {
  DATA_TERMINAL_TRANSITION_GRAPH_MAX_FIELDS,
  DataTerminalTransitionGraph,
  DataTerminalTransitionGraphFieldItem,
  DataTerminalTransitionGraphParams,
  toDataTerminalTransitionGraphSetting,
} from '../domain/dataTerminalTransitionGraph';
import {
  normalizeGraphFieldsForFetch,
  resolveDataTerminalTransitionGraphFields,
  resolveDataTerminalTransitionGraphFieldsForSearch,
} from '../domain/dataTerminalTransitionGraphFields';

import {
  DataTerminalTransitionGraphActionCreators,
  DataTerminalTransitionGraphActionTypes,
  FetchDataTerminalTransitionGraphAction,
  SearchDataTerminalTransitionGraphAction,
  SearchDataTerminalTransitionGraphDateAction,
  SearchDataTerminalTransitionGraphFieldsAction,
  SearchDataTerminalTransitionGraphHallAction,
  SetDataTerminalTransitionGraphFieldsAction,
  dataTerminalTransitionGraphSettingSelector,
} from '../redux/server/dataTerminalTransitionGraph';
import { terminalTransitionReportsSearchConditionSelector } from '../redux/ui/terminalTransitionReportsSetting';
import { Api, buildConfig } from '../utils/api';
import { customSelect } from '../utils/customSelect';
import { handleErrorSaga } from './errorSaga';

/** `/data/terminalTransition/graph` 1回分のレスポンス（field 1項目あたり） */
type GraphApiResponse = {
  setting: DataTerminalTransitionGraphParams;
  data: DataTerminalTransitionGraphFieldItem['data'];
};

type GraphFieldFetchSuccess = {
  field: string;
  response: GraphApiResponse;
};

type GraphFieldFetchFailure = {
  field: string;
  error: unknown;
};

type GraphFieldFetchResult = GraphFieldFetchSuccess | GraphFieldFetchFailure;

const isGraphFieldFetchSuccess = (
  result: GraphFieldFetchResult
): result is GraphFieldFetchSuccess => 'response' in result;

const isGraphFieldFetchFailure = (
  result: GraphFieldFetchResult
): result is GraphFieldFetchFailure => 'error' in result;

const toGraphApiQueryParams = (
  params: DataTerminalTransitionGraphParams,
  field: string
) => {
  const { field: _field, fields: _fields, ...apiParams } = params;
  return { ...apiParams, field };
};

const buildDataTerminalTransitionGraph = (
  responses: GraphApiResponse[],
  fields: string[]
): DataTerminalTransitionGraph => {
  const firstSetting = responses.at(0)?.setting;
  if (!firstSetting) {
    throw new Error('Graph API response is empty');
  }

  const graphList: DataTerminalTransitionGraphFieldItem[] = fields.flatMap(
    (field, index) => {
      const response = responses.at(index);
      if (!response) {
        return [];
      }
      return [{ field, data: response.data }];
    }
  );

  return {
    setting: toDataTerminalTransitionGraphSetting(firstSetting),
    graphList,
  };
};

function* fetchGraphByField(
  api: Api,
  params: DataTerminalTransitionGraphParams,
  field: string
) {
  const response: AxiosResponse<GraphApiResponse> = yield call(
    api.get,
    '/data/terminalTransition/graph',
    buildConfig(toGraphApiQueryParams(params, field))
  );
  return response.data;
}

function* fetchGraphByFieldSafely(
  api: Api,
  params: DataTerminalTransitionGraphParams,
  field: string
): Generator<unknown, GraphFieldFetchResult, GraphApiResponse> {
  try {
    const response = yield call(fetchGraphByField, api, params, field);
    return { field, response };
  } catch (error: unknown) {
    return { field, error };
  }
}

/**
 * 推移グラフ（店舗比較）データ取得処理
 */
function* fetchDataTerminalTransitionGraphSaga(
  api: Api,
  action: FetchDataTerminalTransitionGraphAction
) {
  const { params } = action.payload;
  const fields = resolveDataTerminalTransitionGraphFields(params);

  try {
    yield put(
      DataTerminalTransitionGraphActionCreators.fetchDataTerminalTransitionGraphRequestAction()
    );

    const results: GraphFieldFetchResult[] = yield all(
      fields.map((field) => call(fetchGraphByFieldSafely, api, params, field))
    );

    const successfulResults = results.filter(isGraphFieldFetchSuccess);
    const failedResults = results.filter(isGraphFieldFetchFailure);

    if (successfulResults.length === 0) {
      const firstError = failedResults.at(0)?.error;
      yield put(
        DataTerminalTransitionGraphActionCreators.renewDataTerminalTransitionGraphAction()
      );
      if (firstError !== undefined) {
        yield fork(handleErrorSaga, firstError);
      }
      return;
    }

    const graph = buildDataTerminalTransitionGraph(
      successfulResults.map((result) => result.response),
      successfulResults.map((result) => result.field)
    );

    yield put(
      DataTerminalTransitionGraphActionCreators.fetchDataTerminalTransitionGraphSuccessAction(
        graph
      )
    );

    if (failedResults.length > 0) {
      console.warn(
        `[dataTerminalTransitionGraphSaga] 推移グラフの一部項目の取得に失敗しました: ${failedResults
          .map((result) => result.field)
          .join(', ')}`
      );
    }
  } catch (error: unknown) {
    yield put(
      DataTerminalTransitionGraphActionCreators.renewDataTerminalTransitionGraphAction()
    );
    yield fork(handleErrorSaga, error);
  }
}

function* refetchDataTerminalTransitionGraphWithFields(fields: string[]) {
  const searchCondition: DataTerminalTransitionGraphParams | undefined =
    yield* customSelect(dataTerminalTransitionGraphSettingSelector);

  if (!searchCondition) {
    console.warn(
      '[dataTerminalTransitionGraphSaga] グラフ検索条件が未設定のため再取得をスキップしました'
    );
    return;
  }

  const normalizedFields = normalizeGraphFieldsForFetch(
    fields,
    DATA_TERMINAL_TRANSITION_GRAPH_MAX_FIELDS
  );

  yield put(
    DataTerminalTransitionGraphActionCreators.fetchDataTerminalTransitionGraphAction(
      {
        ...searchCondition,
        fields: normalizedFields,
      }
    )
  );
}

/**
 * クリックしたセルの推移グラフデータを取得する
 */
export function* searchTerminalTransitionGraphSaga(
  action: SearchDataTerminalTransitionGraphAction
) {
  const { shuGroupId, shuList, halls, field } = action.payload;

  const searchCondition = yield* customSelect(
    terminalTransitionReportsSearchConditionSelector
  );

  const existingGraphParams: DataTerminalTransitionGraphParams | undefined =
    yield* customSelect(dataTerminalTransitionGraphSettingSelector);

  // 表示中グラフからは項目一覧のみ引き継ぎ、期間などの条件は検索フォームの値を優先する
  const fields = field
    ? [field]
    : resolveDataTerminalTransitionGraphFieldsForSearch(existingGraphParams);

  yield put(
    DataTerminalTransitionGraphActionCreators.fetchDataTerminalTransitionGraphAction(
      {
        ...searchCondition,
        fields,
        shuGroupIds: shuGroupId,
        shuList,
        halls,
      }
    )
  );
}

/**
 * 指定した店舗の推移グラフ（店舗比較）データを再取得する
 */
function* searchTerminalTransitionGraphHallSaga(
  action: SearchDataTerminalTransitionGraphHallAction
) {
  const { hall } = action.payload;

  const graphSearchCondition: DataTerminalTransitionGraphParams | undefined =
    yield* customSelect(dataTerminalTransitionGraphSettingSelector);

  if (!graphSearchCondition) {
    return;
  }

  yield put(
    DataTerminalTransitionGraphActionCreators.fetchDataTerminalTransitionGraphAction(
      {
        ...graphSearchCondition,
        halls: hall,
      }
    )
  );
}

/**
 * 指定したグラフ項目の推移グラフデータを再取得する
 */
function* searchTerminalTransitionGraphFieldsSaga(
  action: SearchDataTerminalTransitionGraphFieldsAction
) {
  yield* refetchDataTerminalTransitionGraphWithFields([action.payload.field]);
}

/**
 * 表示グラフ項目を配列で一括変更して再取得する。
 */
function* setDataTerminalTransitionGraphFieldsSaga(
  action: SetDataTerminalTransitionGraphFieldsAction
) {
  yield* refetchDataTerminalTransitionGraphWithFields(action.payload.fields);
}

/**
 * 指定した期間で推移グラフ（店舗比較）データを再取得する
 */
function* searchTerminalTransitionGraphDateSaga(
  action: SearchDataTerminalTransitionGraphDateAction
) {
  const { startDate, endDate } = action.payload;

  const graphSearchCondition: DataTerminalTransitionGraphParams | undefined =
    yield* customSelect(dataTerminalTransitionGraphSettingSelector);

  if (!graphSearchCondition) {
    return;
  }

  yield put(
    DataTerminalTransitionGraphActionCreators.fetchDataTerminalTransitionGraphAction(
      {
        ...graphSearchCondition,
        startDate,
        endDate,
      }
    )
  );
}

function* handleFetchTerminalTransitionGraphSaga(api: Api) {
  yield takeLatest(
    DataTerminalTransitionGraphActionTypes.FETCH_DATA_TERMINAL_TRANSITION_GRAPH,
    fetchDataTerminalTransitionGraphSaga,
    api
  );
}

function* handleSearchSaga() {
  yield takeLatest(
    DataTerminalTransitionGraphActionTypes.SEARCH_DATA_TERMINAL_TRANSITION_GRAPH,
    searchTerminalTransitionGraphSaga
  );
  yield takeLatest(
    DataTerminalTransitionGraphActionTypes.SEARCH_DATA_TERMINAL_TRANSITION_GRAPH_HALL,
    searchTerminalTransitionGraphHallSaga
  );
  yield takeLatest(
    DataTerminalTransitionGraphActionTypes.SEARCH_DATA_TERMINAL_TRANSITION_GRAPH_FIELDS,
    searchTerminalTransitionGraphFieldsSaga
  );
  yield takeLatest(
    DataTerminalTransitionGraphActionTypes.SET_DATA_TERMINAL_TRANSITION_GRAPH_FIELDS,
    setDataTerminalTransitionGraphFieldsSaga
  );
  yield takeLatest(
    DataTerminalTransitionGraphActionTypes.SEARCH_DATA_TERMINAL_TRANSITION_GRAPH_DATE,
    searchTerminalTransitionGraphDateSaga
  );
}

export function* dataTerminalTransitionGraphSaga(context: { api: Api }) {
  yield fork(handleFetchTerminalTransitionGraphSaga, context.api);
  yield fork(handleSearchSaga);
}
